import { useEffect, useMemo, useState } from 'react';
import { Card, Checkbox, Select, Typography } from 'antd';
import { api } from '../lib/api';

const { Text } = Typography;

type OrgOption = {
  id: string;
  name: string;
  slug: string;
};

type UserOption = {
  id: string;
  name: string | null;
  email: string;
};

type OrganizationMemberRecord = {
  user: UserOption | null;
};

type AnalyticsFiltersProps = {
  ignoreAnnea: boolean;
  organizationId: string | null;
  userId: string | null;
  onIgnoreAnneaChange: (value: boolean) => void;
  onOrganizationChange: (value: string | null) => void;
  onUserChange: (value: string | null) => void;
};

export default function AnalyticsFilters({
  ignoreAnnea,
  organizationId,
  userId,
  onIgnoreAnneaChange,
  onOrganizationChange,
  onUserChange,
}: AnalyticsFiltersProps) {
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState<UserOption[]>([]);
  const [orgUserLoading, setOrgUserLoading] = useState(false);
  const [globalUsers, setGlobalUsers] = useState<UserOption[]>([]);
  const [globalUserLoading, setGlobalUserLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadOrganizations = async () => {
      setOrgLoading(true);
      try {
        const data = await api.admin.listOrganizations();
        const items = Array.isArray(data.organizations) ? data.organizations : [];
        if (isMounted) {
          setOrganizations(items);
        }
      } catch (error) {
        console.error('[AnalyticsFilters] Failed to load organizations', error);
        if (isMounted) {
          setOrganizations([]);
        }
      } finally {
        if (isMounted) {
          setOrgLoading(false);
        }
      }
    };
    loadOrganizations();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadGlobalUsers = async () => {
      setGlobalUserLoading(true);
      try {
        const data = await api.analytics.users();
        const users = Array.isArray(data.users) ? data.users : [];
        if (isMounted) {
          setGlobalUsers(users);
        }
      } catch (error) {
        console.error('[AnalyticsFilters] Failed to load users', error);
        if (isMounted) {
          setGlobalUsers([]);
        }
      } finally {
        if (isMounted) {
          setGlobalUserLoading(false);
        }
      }
    };
    loadGlobalUsers();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadOrgUsers = async () => {
      if (!organizationId) {
        if (isMounted) {
          setOrgUsers([]);
        }
        return;
      }
      setOrgUserLoading(true);
      try {
        const data = await api.admin.listOrganizationMembers(organizationId);
        const items = Array.isArray(data.members) ? data.members as OrganizationMemberRecord[] : [];
        const mapped = items
          .map((member) => member.user)
          .filter((user): user is UserOption => Boolean(user));
        if (isMounted) {
          setOrgUsers(mapped);
        }
      } catch (error) {
        console.error('[AnalyticsFilters] Failed to load organization members', error);
        if (isMounted) {
          setOrgUsers([]);
        }
      } finally {
        if (isMounted) {
          setOrgUserLoading(false);
        }
      }
    };
    loadOrgUsers();
    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  useEffect(() => {
    const available = organizationId ? orgUsers : globalUsers;
    if (userId && !available.some((user) => user.id === userId)) {
      onUserChange(null);
    }
  }, [organizationId, orgUsers, globalUsers, userId, onUserChange]);

  const orgOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id,
      label: org.name,
    }));
  }, [organizations]);

  const availableUsers = organizationId ? orgUsers : globalUsers;
  const userOptions = useMemo(() => {
    return availableUsers.map((user) => ({
      value: user.id,
      label: user.name ? `${user.name} (${user.email})` : user.email,
    }));
  }, [availableUsers]);

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <Checkbox
            checked={ignoreAnnea}
            onChange={(event) => onIgnoreAnneaChange(event.target.checked)}
          >
            Ignore Annea users
          </Checkbox>
        </div>
        <div style={{ minWidth: 240 }}>
          <Text type="secondary">Organization</Text>
          <Select
            style={{ width: '100%' }}
            value={organizationId || undefined}
            onChange={(value) => onOrganizationChange(value || null)}
            allowClear
            loading={orgLoading}
            placeholder="All organizations"
            options={orgOptions}
            showSearch
            optionFilterProp="label"
          />
        </div>
        <div style={{ minWidth: 240 }}>
          <Text type="secondary">User</Text>
          <Select
            style={{ width: '100%' }}
            value={userId || undefined}
            onChange={(value) => onUserChange(value || null)}
            allowClear
            loading={organizationId ? orgUserLoading : globalUserLoading}
            placeholder="All users"
            options={userOptions}
            showSearch
            optionFilterProp="label"
          />
        </div>
      </div>
    </Card>
  );
}
